import { useEffect, useState } from "react";
import { Search, Users, X } from "lucide-react";

import { api } from "@/lib/api";
import { useToast } from "@/components/ui/toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Loader from "@/components/ui/loader";
import { Pagination } from "@/components/ui/pagination";
import {
  EmptyPanel,
  PageHero,
  PagePanel,
  PageShell,
  PanelHeading,
} from "@/components/interior-page";

const PAGE_SIZE = 15;

interface AdvisorSummary {
  advisor_id: string;
  first_name: string;
  last_name: string;
  email: string;
  code: string | null;
  total_buyers: number;
}

interface ReferredBuyer {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  status: string;
  created_at: string;
}

const STATUS_COLORS: Record<string, string> = {
  active: "bg-emerald-100 text-emerald-700",
  inactive: "bg-slate-100 text-slate-600",
  pending_verification: "bg-amber-100 text-amber-700",
};

const STATUS_LABELS: Record<string, string> = {
  active: "Active",
  inactive: "Inactive",
  pending_verification: "Pending",
};

export default function AdminReferralsPage() {
  const toast = useToast();
  const [ready, setReady] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [advisors, setAdvisors] = useState<AdvisorSummary[]>([]);
  const [advisorsTotal, setAdvisorsTotal] = useState(0);
  const [totalBuyers, setTotalBuyers] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");

  // Drill-down modal state
  const [selected, setSelected] = useState<AdvisorSummary | null>(null);
  const [modalBuyers, setModalBuyers] = useState<ReferredBuyer[]>([]);
  const [modalTotal, setModalTotal] = useState(0);
  const [modalPage, setModalPage] = useState(1);
  const [modalLoading, setModalLoading] = useState(false);

  // Debounce the search box, resetting to the first page on each new term.
  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      setAppliedSearch(search.trim());
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    setFetching(true);
    const params = new URLSearchParams({
      skip: String((page - 1) * PAGE_SIZE),
      limit: String(PAGE_SIZE),
    });
    if (appliedSearch) params.set("search", appliedSearch);
    api
      .get<{ total_elements: number; total_buyers: number; data: AdvisorSummary[] }>(
        `/referrals/admin/summary?${params.toString()}`,
      )
      .then((res) => {
        if (cancelled) return;
        setAdvisors(res.data);
        setAdvisorsTotal(res.total_elements);
        setTotalBuyers(res.total_buyers);
      })
      .catch(() => {
        if (!cancelled) toast.error("Failed to load advisor referrals");
      })
      .finally(() => {
        if (!cancelled) {
          setReady(true);
          setFetching(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [page, appliedSearch, toast]);

  // Load a page of the selected advisor's buyers.
  useEffect(() => {
    if (!selected) return;
    let cancelled = false;
    setModalLoading(true);
    api
      .get<{ total_elements: number; data: ReferredBuyer[] }>(
        `/referrals/admin/${selected.advisor_id}/buyers?skip=${(modalPage - 1) * PAGE_SIZE}&limit=${PAGE_SIZE}`,
      )
      .then((res) => {
        if (cancelled) return;
        setModalBuyers(res.data);
        setModalTotal(res.total_elements);
      })
      .catch(() => {
        if (!cancelled) toast.error("Failed to load buyers for this advisor");
      })
      .finally(() => {
        if (!cancelled) setModalLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selected, modalPage, toast]);

  function openAdvisor(advisor: AdvisorSummary) {
    setSelected(advisor);
    setModalBuyers([]);
    setModalTotal(0);
    setModalPage(1);
  }

  if (!ready) return <Loader className="min-h-[60vh]" />;

  return (
    <PageShell>
      <PageHero
        eyebrow="BL Advisor Referrals"
        title="Buyer referrals by advisor"
        description="Every BL Advisor and the number of buyers who created an account through their referral link. Open an advisor to see their referred buyers."
        aside={
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Users className="h-5 w-5" />
            </div>
            <div>
              <div className="text-2xl font-extrabold tracking-tight text-foreground">{totalBuyers}</div>
              <div className="text-[0.68rem] font-bold uppercase tracking-[0.16em] text-muted-foreground">
                Referred buyers total
              </div>
            </div>
          </div>
        }
      />

      <PagePanel>
        <PanelHeading
          title="Advisors"
          description={`${advisorsTotal} BL ${advisorsTotal === 1 ? "Advisor" : "Advisors"}${appliedSearch ? " matching your search" : ""}`}
          actions={
            <div className="relative w-full sm:w-72">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/60" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, email, or code"
                className="pl-9 pr-9"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-full p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          }
        />
        {fetching && advisors.length === 0 ? (
          <Loader className="min-h-[30vh]" size="sm" />
        ) : advisors.length === 0 ? (
          <EmptyPanel
            icon={Users}
            title={appliedSearch ? "No advisors match your search" : "No BL Advisors yet"}
            description={
              appliedSearch
                ? "Try a different name, email, or referral code."
                : "Once BL Advisors register, their referral performance will appear here."
            }
          />
        ) : (
          <div className="relative overflow-x-auto">
            {fetching && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-card/60 backdrop-blur-[1px]">
                <Loader size="sm" />
              </div>
            )}
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-border/80 text-left text-[0.7rem] font-bold uppercase tracking-[0.12em] text-muted-foreground">
                  <th className="px-3 py-2.5">BL Advisor</th>
                  <th className="px-3 py-2.5">Email</th>
                  <th className="px-3 py-2.5">Code</th>
                  <th className="px-3 py-2.5 text-center">Total buyers</th>
                  <th className="px-3 py-2.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {advisors.map((a) => (
                  <tr key={a.advisor_id} className="border-b border-border/50 last:border-0 hover:bg-muted/20">
                    <td className="px-3 py-3 font-semibold text-foreground">
                      {a.first_name} {a.last_name}
                    </td>
                    <td className="px-3 py-3 text-muted-foreground">{a.email}</td>
                    <td className="px-3 py-3">
                      <span className="font-mono text-xs font-semibold text-foreground">{a.code || "—"}</span>
                    </td>
                    <td className="px-3 py-3 text-center">
                      <span className="inline-flex min-w-[2rem] items-center justify-center rounded-full bg-primary/10 px-2 py-0.5 text-sm font-bold text-primary">
                        {a.total_buyers}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-primary/20 bg-white/72 hover:bg-white"
                        onClick={() => openAdvisor(a)}
                        disabled={a.total_buyers === 0}
                      >
                        Show more
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination
              page={page}
              totalElements={advisorsTotal}
              pageSize={PAGE_SIZE}
              onChange={setPage}
              className="mt-4"
            />
          </div>
        )}
      </PagePanel>

      {/* Drill-down modal */}
      {selected && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
          onClick={() => setSelected(null)}
        >
          <div
            className="max-h-[80dvh] w-full max-w-2xl overflow-hidden rounded-2xl border border-border/80 bg-card shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 border-b border-border/70 px-5 py-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">
                {selected.first_name?.[0]}
                {selected.last_name?.[0]}
              </div>
              <div className="min-w-0">
                <div className="font-bold text-foreground">
                  {selected.first_name} {selected.last_name}
                </div>
                <div className="truncate text-sm text-muted-foreground">
                  {selected.total_buyers} referred {selected.total_buyers === 1 ? "buyer" : "buyers"}
                </div>
              </div>
              <button
                onClick={() => setSelected(null)}
                className="ml-auto shrink-0 rounded-full p-1.5 hover:bg-muted"
                aria-label="Close"
              >
                <X className="h-5 w-5 text-muted-foreground" />
              </button>
            </div>

            <div className="max-h-[60dvh] overflow-y-auto px-5 py-4">
              {modalLoading ? (
                <Loader className="min-h-[20vh]" />
              ) : modalBuyers.length === 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground">No referred buyers found.</p>
              ) : (
                <div className="overflow-x-auto">
                <table className="w-full min-w-[420px] border-collapse text-sm">
                  <thead>
                    <tr className="border-b border-border/80 text-left text-[0.7rem] font-bold uppercase tracking-[0.12em] text-muted-foreground">
                      <th className="px-2 py-2">Buyer</th>
                      <th className="px-2 py-2">Email</th>
                      <th className="px-2 py-2">Status</th>
                      <th className="px-2 py-2">Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {modalBuyers.map((b) => (
                      <tr key={b.id} className="border-b border-border/50 last:border-0">
                        <td className="px-2 py-2.5 font-semibold text-foreground">
                          {b.first_name} {b.last_name}
                        </td>
                        <td className="px-2 py-2.5 text-muted-foreground">{b.email}</td>
                        <td className="px-2 py-2.5">
                          <span
                            className={`rounded-full px-2 py-0.5 text-xs font-bold ${
                              STATUS_COLORS[b.status] || "bg-slate-100 text-slate-600"
                            }`}
                          >
                            {STATUS_LABELS[b.status] || b.status}
                          </span>
                        </td>
                        <td className="px-2 py-2.5 text-muted-foreground">
                          {new Date(b.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              )}
            </div>

            {modalTotal > PAGE_SIZE && (
              <div className="border-t border-border/70 px-5 py-3">
                <Pagination
                  page={modalPage}
                  totalElements={modalTotal}
                  pageSize={PAGE_SIZE}
                  onChange={setModalPage}
                  className="border-t-0 pt-0"
                />
              </div>
            )}
          </div>
        </div>
      )}
    </PageShell>
  );
}
